<template>
    <AxSkeleton
        v-if="isFetching"
        width="8rem"
        height="2rem"
        borderRadius="6px"
    />
    <AxButton
        v-else
        :label="folderLabel"
        :icon="folderIcon"
        severity="secondary"
        variant="outlined"
        size="small"
        @mouseenter="openPopover"
        @mouseleave="closePopover"
        @click="openNewWindow"
    >
        <div class="flex max-w-60 items-center gap-1 overflow-hidden">
            <AxFileIcon :icon="folderIcon" />
            <small ref="linkRef" class="truncate text-blue-500">
                {{ folderLabel }}
            </small>
        </div>
    </AxButton>

    <AxPopover ref="popoverRef" @show="handleShow" @hide="handleHide">
        <div class="flex flex-col gap-4">
            <div class="flex items-center gap-2">
                <AxFileIcon :icon="folderIcon" />
                <small>
                    {{ folderLabel }}
                </small>
            </div>
            <AxTag
                class="w-fit"
                icon="ax ax-workflows"
                :label="workflowLabel"
                severity="secondary"
            />

            <div v-if="folderPath" class="flex flex-col gap-2">
                <small class="text-secondary-color">
                    {{ $t('workspace.folder_button.location', { isFolder: isFolder ? 'folder' : 'file' }) }}
                </small>
                <p>
                    {{ folderPath }}
                </p>
            </div>
        </div>
    </AxPopover>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { AxButton, AxTag, AxPopover, AxSkeleton } from '@axxone-system/hades';
import AxFileIcon from './AxFileIcon.vue';
import { useQuery } from '@tanstack/vue-query';
import { useBlocksUIApi } from '@/shared/API/UI';
import { useFolderHelper } from '@/shared/utils/folderHelper';
import { useI18n } from 'vue-i18n';
import { useFileHelper } from '@/shared/utils/fileHelper';


defineOptions({
    customOptions: {
        name: 'AxFolderButton',
    },
});

const props = defineProps({
    folderId: {
        type: Number,
        required: true,
    },
});

const router = useRouter();
const linkRef = ref<HTMLElement | null>(null);

const { t } = useI18n();
const blocksUIApi = useBlocksUIApi();
const { getIcon: getFolderIcon } = useFolderHelper();
const { getIcon: getFileIcon } = useFileHelper();

const enabled = computed(() => props.folderId > 0);

const abortControllerRef = ref<AbortController | null>(null);

const { data: folderButtonResponse, isFetching } = useQuery({
    queryKey: computed(() => ['get_folder_button_info', props.folderId]),
    queryFn: async ({ signal }) => {
        if (abortControllerRef.value) {
            abortControllerRef.value.abort();
        }
        const abortController = new AbortController();
        abortControllerRef.value = abortController;
        signal?.addEventListener('abort', () => abortController.abort());

        return blocksUIApi.getFolderButtonInfo({
            folderId: Number(props.folderId),
            signal: abortController.signal,
        });
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
});

const folderData = computed(() => folderButtonResponse.value?.data ?? null);

const folderLabel = computed(() => folderData.value?.label ?? '');

const folderPath = computed(() => folderData.value?.path.join(' / ') ?? '');

const isFolder = computed(() => folderData.value?.isFolder);

const encodedPath = computed(() => btoa(folderData.value!.idPath.join('&')));

const folderIcon = computed(() => {
    const data = folderData.value;
    if (!data) {
        return 'ax ax-folder-color';
    }
    if (!isFolder.value) {
        return getFileIcon(data.extension);
    }
    return getFolderIcon({
        edcType: data.edc?.type ?? 0,
        ssType: '',
        hasCnOrWk: !!data.workflow || !!data.cn,
        hasQualif: false,
    }) ?? 'ax ax-folder-color';
});

const workflowLabel = computed(() => {
    return folderData.value?.workflow ? folderData.value?.workflow.label : t('workspace.folder_button.no_workflow');
});

// ───── Popover ─────
const popoverRef = ref<InstanceType<typeof AxPopover> | null>(null);

function openPopover(event: MouseEvent) {
    if (linkRef.value) {
        popoverRef.value?.show(event, linkRef.value, 'hover');
    }
}

function closePopover(event: MouseEvent) {
    if (linkRef.value) {
        popoverRef.value?.hide(event, linkRef.value);
    }
}

function handleShow() {
    linkRef.value?.style.setProperty('text-decoration', 'underline');
}

function handleHide() {
    linkRef.value?.style.setProperty('text-decoration', 'none');
}

onUnmounted(() => {
    abortControllerRef.value?.abort();
});

// ───── Methods ─────
function openNewWindow() {
    if (!folderData.value || !encodedPath.value) {
        return;
    }
    const workspaceId = folderData.value.idPath[0];
    const { href } = router.resolve({
        name: 'workspace',
        params: {
            workspaceId,
        },
        query: {
            path: encodedPath.value,
        },
    });
    window.open(href, '_blank');
}
</script>
